import { ReportsUtils } from '../../utils/reportsUtils';
import { applyFilterCfg } from '../../components/financialTables/configuration/applyUtill';
import Utils from '../../utils/descriptor/descriptorUtils';
import DescriptorType from '../../utils/descriptor/descriptorType';
import { chartableDescriptorsMatcher } from '../../components/chart/utils';
import { getCategorizedDescriptors } from '../../utils/categorization/descriptorsCategorization';
import { getCategorizedReports } from '../../utils/categorization/reportsCategorization';
import CollectionUtils from '../../utils/collections';
import { LABEL_FORMATS } from '../../components/financialTables/configuration/constants';
import { DescriptorProps } from '../../constnats/descriptor';
import { SchemaDescriptor } from './types';

// ─── Descriptor label resolution ─────────────────────────────────────────────

const LABELS_FORMAT_DESCRIPTOR_PROPS: Record<string, string> = {
    [LABEL_FORMATS.DEFAULT]: DescriptorProps.LABEL,
};

export function getDescriptorLabel(
    descriptor: SchemaDescriptor,
    categoryBasedMode: boolean,
    labelsFormat: string,
    standardizedLabel?: { name: string },
): string {
    const category = descriptor[DescriptorProps.CATEGORY] as { name: string } | undefined;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (category && categoryBasedMode && !DescriptorType.isHeadline(descriptor as any)) {
        return category.name;
    }
    if (labelsFormat === LABEL_FORMATS.STANDARDIZED) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const relation = (descriptor[DescriptorProps.STANDARDIZED_LABEL_RELATION] as any);
        return relation?.standardizedLabelOverwrite
            || standardizedLabel?.name
            || descriptor[DescriptorProps.LABEL] as string;
    }
    const prop = LABELS_FORMAT_DESCRIPTOR_PROPS[labelsFormat];
    if (prop) {
        return (descriptor[prop] as string) || descriptor[DescriptorProps.LABEL] as string;
    }
    return descriptor[DescriptorProps.LABEL] as string;
}

// ─── Calculated reports & category mode ──────────────────────────────────────

export function prepareReportsAndDescriptors(
    schemaDescriptors: SchemaDescriptor[],
    rawReports: unknown[],
    categoryBasedMode: boolean,
): { dynamicFlatDescriptors: SchemaDescriptor[]; reports: unknown[] } {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const flatDynamic = Utils.flatDescriptors(schemaDescriptors as any, true);
    const reportsUtil = new ReportsUtils(flatDynamic);
    // When duplicate calculated reports exist for the same period, keep the latest — earlier values are intermediate.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const takeLatest = (_prev: any, next: any) => next;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const calculatedReports = reportsUtil.getCalculatedReports(rawReports as any, takeLatest, false);
    const allReports = rawReports.concat(calculatedReports);

    if (categoryBasedMode) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const categorizedTree = getCategorizedDescriptors(schemaDescriptors as any) as SchemaDescriptor[];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const categorizedFlat = Utils.flatDescriptors(categorizedTree as any) as SchemaDescriptor[];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const nonCategorizedMap = CollectionUtils.arrToMap(flatDynamic, (d: any) => d.id);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const categorizedReports = getCategorizedReports(categorizedFlat, allReports as any, nonCategorizedMap);
        const categorizedDynamicFlatDescriptors = categorizedFlat.filter((d) => Utils.isDynamic(d));
        return { dynamicFlatDescriptors: categorizedDynamicFlatDescriptors, reports: categorizedReports };
    }

    return { dynamicFlatDescriptors: flatDynamic as SchemaDescriptor[], reports: allReports };
}

// ─── Report processing ───────────────────────────────────────────────────────

export function processReports(
    rawReports: unknown[],
    flatDescriptors: SchemaDescriptor[],
    filterCfg: Record<string, unknown>,
    tableConfig: Record<string, unknown>,
): unknown[] {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const filtered = applyFilterCfg(rawReports as any, filterCfg, tableConfig, flatDescriptors) as unknown[];
    ReportsUtils.sort(filtered, false);
    return filtered;
}

export function filterChartable(flatDescriptors: SchemaDescriptor[]): SchemaDescriptor[] {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return flatDescriptors.filter((d) => chartableDescriptorsMatcher(d as any));
}

export function getChartableDescriptors(descriptors: SchemaDescriptor[]): SchemaDescriptor[] {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const flat = Utils.flatDescriptors(descriptors as any, true) as SchemaDescriptor[];
    return filterChartable(flat);
}
